import { useState } from 'react';
import type { DocumentMeta, ExportSettings } from '../../core/types';
import { estimatedPixelCount } from '../../io/export';
import { exportActiveDocument } from '../../state/actions';
import { Modal, NumberInput, Select, SliderRow } from '../components/controls';

export function ExportDialog({
  doc,
  open,
  onClose,
}: {
  doc: DocumentMeta;
  open: boolean;
  onClose: () => void;
}) {
  const [format, setFormat] = useState<ExportSettings['format']>('png');
  const [quality, setQuality] = useState(92);
  const [scalePercent, setScalePercent] = useState(100);
  const [busy, setBusy] = useState(false);

  const scale = scalePercent / 100;

  const run = async () => {
    setBusy(true);
    await exportActiveDocument({ format, quality: quality / 100, scale });
    setBusy(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Export Image"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={busy}
            onClick={() => void run()}
          >
            {busy ? 'Encoding…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Format
        </span>
        <Select<ExportSettings['format']>
          value={format}
          options={[
            { value: 'png', label: 'PNG — lossless, with transparency' },
            { value: 'jpeg', label: 'JPEG — lossy, no transparency' },
            { value: 'webp', label: 'WebP — lossy, with transparency' },
            { value: 'pdf', label: 'PDF — flattened page (JPEG)' },
          ]}
          onChange={setFormat}
        />
      </div>

      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Scale
        </span>
        <NumberInput
          value={scalePercent}
          min={1}
          max={400}
          onChange={setScalePercent}
          width={90}
        />
        <span className="field__label">% → {estimatedPixelCount(doc, scale)} px</span>
      </div>

      {format !== 'png' && (
        <SliderRow label="Quality" value={quality} min={1} max={100} onChange={setQuality} />
      )}

      {(format === 'jpeg' || format === 'pdf') && (
        <p className="dialog__note">
          {format === 'pdf'
            ? 'PDF export embeds a JPEG of the flattened document; transparent areas are filled with white.'
            : 'JPEG has no alpha channel: transparent areas will be filled with white.'}
        </p>
      )}
    </Modal>
  );
}
